import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { COUNT_UP_MS, easeOut } from './countUp.js';

/**
 * Progress (0..1) for a stats bar that counts up when it scrolls into
 * view. 1 — the plain numbers — whenever the option is off, the reader
 * asked for reduced motion, or the browser cannot tell us when the bar
 * is on screen: a count that never starts is worse than no count.
 * Turning the option on in the editor plays it again.
 */
export function useCountUp(rootRef, enabled) {
    const progress = ref(1);
    let observer = null;
    let frame = 0;

    const reduced = () => typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    function stop() {
        observer?.disconnect();
        observer = null;
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
    }

    function run() {
        const start = performance.now();
        const step = (t) => {
            progress.value = easeOut((t - start) / COUNT_UP_MS);
            frame = progress.value < 1 ? requestAnimationFrame(step) : 0;
        };
        frame = requestAnimationFrame(step);
    }

    function arm() {
        stop();
        const el = rootRef.value;
        if (!enabled() || reduced() || !el || typeof IntersectionObserver === 'undefined') {
            progress.value = 1;
            return;
        }
        progress.value = 0;
        observer = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            observer?.disconnect();
            observer = null;
            run();
        }, { threshold: 0.3 });
        observer.observe(el);
    }

    onMounted(arm);
    watch(() => Boolean(enabled()), arm);
    onBeforeUnmount(stop);

    return progress;
}
